import React from 'react';
import { fetchAllWords } from '../services/wordService';

const ArrowIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const SettingsIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

const HomeScreen = ({
  theme,
  setTheme,
  user,
  getGreeting,
  setStep,
  setGameType,
  handleOpenProfile,
  muted,
  toggleSoundMute
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [isDictionaryOpen, setIsDictionaryOpen] = React.useState(false);
  const [dictionarySearch, setDictionarySearch] = React.useState("");
  const isDark = theme === "dark";

  const [dbWords, setDbWords] = React.useState(null);

  React.useEffect(() => {
    fetchAllWords().then(setDbWords);
  }, []);

  const wotd = React.useMemo(() => {
    const wordPool = dbWords;
    if (!wordPool || wordPool.length === 0) return { word: "Learning...", meaning: "Fetching words from database" };

    const today = new Date().toISOString().split("T")[0];
    let hash = 0;
    for (let i = 0; i < today.length; i++) {
      hash = (hash << 5) - hash + today.charCodeAt(i);
      hash |= 0;
    }
    return wordPool[Math.abs(hash) % wordPool.length];
  }, [dbWords]);

  const gamesGoal = 3;
  const successfulGames = user.successfulGamesCount || 0;

  const dynamicStyles = getStyles(theme);

  const filteredWords = React.useMemo(() => {
    if (!dbWords) return [];
    if (!dictionarySearch.trim()) return dbWords;
    const query = dictionarySearch.toLowerCase().trim();
    return dbWords.filter(w =>
      w.word.toLowerCase().includes(query) ||
      w.meaning.toLowerCase().includes(query)
    );
  }, [dbWords, dictionarySearch]);

  const colors = {
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#0F172A",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    border: isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0"
  };

  return (
    <div style={dynamicStyles.container}>
      {/* Top Header */}
      <div style={dynamicStyles.topBar}>
        <div style={dynamicStyles.greetingLeft}>
          <div style={dynamicStyles.greetingLabel}>{(getGreeting() || "GOOD AFTERNOON").toUpperCase()}</div>
          <div style={dynamicStyles.usernameText}>{(user.username || "User").toUpperCase()}</div>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            onClick={() => setIsSettingsOpen(true)}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: isDark ? "#1E293B" : "#1AA39A",
              border: isDark ? "1.5px solid #334155" : "1.5px solid white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              color: "white"
            }}
          >
            <SettingsIcon />
          </button>

          <button
            onClick={handleOpenProfile}
            style={dynamicStyles.profileCircle}
          >
            <div style={dynamicStyles.avatarPlaceholder}>{(user.username || "P")[0].toUpperCase()}</div>
          </button>
        </div>
      </div>

      <div style={dynamicStyles.content}>
        <div style={dynamicStyles.heroCard}>
          {/* Left Stats */}
          <div style={dynamicStyles.heroLeft}>
            <div style={dynamicStyles.heroLabel}>YOUR STREAK</div>
            <div style={dynamicStyles.heroValue}>
              <span style={{
                filter: (successfulGames >= 1) ? "none" : "grayscale(100%)",
                opacity: (successfulGames >= 1) ? 1 : 0.4,
                transition: "all 0.3s ease",
                display: "inline-block",
                fontSize: "32px"
              }}>
                🔥
              </span>
              {user.currentStreak || 0}
            </div>
            <div style={dynamicStyles.heroSubtext}>Keep it going!</div>
          </div>

          {/* Right Stats */}
          <div style={dynamicStyles.heroRight}>
            <div style={dynamicStyles.heroLabel}>DAILY WORKOUT</div>

            <div style={{
              ...dynamicStyles.heroRing,
              background: successfulGames >= gamesGoal
                ? '#14b8a6'
                : `conic-gradient(#14b8a6 ${(successfulGames / gamesGoal) * 360}deg, ${isDark ? '#334155' : '#e5e7eb'} 0deg)`
            }}>
              <div style={dynamicStyles.heroRingInner}>
                {successfulGames >= gamesGoal ? (
                  <CheckIcon size={32} color="#1AA39A" />
                ) : (
                  <><span style={{ color: '#1AA39A' }}>{successfulGames}</span>/{gamesGoal}</>
                )}
              </div>
            </div>

            <div style={dynamicStyles.heroSubtextTeal}>
              {successfulGames === 0 && "Start Workout"}
              {successfulGames === 1 && "2 More to Finish"}
              {successfulGames === 2 && "1 More for Reward 🔥"}
              {successfulGames >= gamesGoal && "Well Done!"}
            </div>
          </div>

          {/* Absolute Brain */}
          <img
            src="/images/homepage/brain.webp?v=2"
            alt="Brain"
            style={dynamicStyles.heroBrain}
          />
        </div>

        {/* 3. Section Header */}
        <div id="pick-a-game" style={dynamicStyles.sectionHeader}>
          Pick a Game
        </div>

        {/* 4. Game List */}
        <div style={dynamicStyles.gameList}>
          {/* Meaning Match (Position 1 - Green) */}
          <div
            style={{ ...dynamicStyles.gameCard, background: isDark ? '#064E3B' : '#E8F8F5', border: isDark ? '1px solid #065F46' : 'none' }}
            onClick={() => { setGameType("meaning"); setStep("input"); }}
          >
            <div style={dynamicStyles.gameIconBoxFixed}>
              <img src="/images/homepage/mm_icon.webp" alt="Meaning Match" style={dynamicStyles.gameIcon} />
            </div>
            <div style={dynamicStyles.gameInfo}>
              <div style={dynamicStyles.gameTitle}>Meaning Match</div>
              <div style={dynamicStyles.gameDesc}>Pick the right definition</div>
            </div>
            <div style={{ ...dynamicStyles.gameArrow, background: '#1AA39A' }}>
              <ArrowIcon size={20} />
            </div>
          </div>

          {/* Tone Clash (Position 2 - Blue) */}
          <div
            style={{ ...dynamicStyles.gameCard, background: isDark ? '#1E3A8A' : '#DBEAFE', border: isDark ? '1px solid #1E40AF' : 'none' }}
            onClick={() => { setGameType("tone_clash"); setStep("input"); }}
          >
            <div style={dynamicStyles.gameIconBoxFixed}>
              <img src="/images/homepage/tc_icon.webp?v=2" alt="Tone Clash" style={dynamicStyles.gameIcon} />
            </div>
            <div style={dynamicStyles.gameInfo}>
              <div style={dynamicStyles.gameTitle}>Tone Clash</div>
              <div style={dynamicStyles.gameDesc}>Spot the word that clashes</div>
            </div>
            <div style={{ ...dynamicStyles.gameArrow, background: '#3B82F6' }}>
              <ArrowIcon size={20} />
            </div>
          </div>

          {/* Visualization (Position 3 - Orange) */}
          <div
            style={{ ...dynamicStyles.gameCard, background: isDark ? '#78350F' : '#FDEBD0', border: isDark ? '1px solid #92400E' : 'none' }}
            onClick={() => { setGameType("visualization"); setStep("input"); }}
          >
            <div style={dynamicStyles.gameIconBoxFixed}>
              <img src="/images/homepage/visual icon.webp" alt="Visualization" style={dynamicStyles.gameIcon} />
            </div>
            <div style={dynamicStyles.gameInfo}>
              <div style={dynamicStyles.gameTitle}>Visualization</div>
              <div style={dynamicStyles.gameDesc}>See the word in action</div>
            </div>
            <div style={{ ...dynamicStyles.gameArrow, background: '#F0997B' }}>
              <ArrowIcon size={20} />
            </div>
          </div>

          {/* Connotations (Position 4 - Purple) */}
          <div
            style={{ ...dynamicStyles.gameCard, background: isDark ? '#4C1D95' : '#EDE9FE', border: isDark ? '1px solid #5B21B6' : 'none' }}
            onClick={() => { setGameType("connotation"); setStep("input"); }}
          >
            <div style={dynamicStyles.gameIconBoxFixed}>
              <img src="/images/homepage/con_icon.webp" alt="Connotations" style={dynamicStyles.gameIcon} />
            </div>
            <div style={dynamicStyles.gameInfo}>
              <div style={dynamicStyles.gameTitle}>Connotations</div>
              <div style={dynamicStyles.gameDesc}>Positive or Negative?</div>
            </div>
            <div style={{ ...dynamicStyles.gameArrow, background: '#A855F7' }}>
              <ArrowIcon size={20} />
            </div>
          </div>

          {/* Match the Twins (Position 5 - Pink) */}
          <div
            style={{ ...dynamicStyles.gameCard, background: isDark ? '#831843' : '#FCE7F3', border: isDark ? '1px solid #9D174D' : 'none' }}
            onClick={() => { setGameType("twins"); setStep("input"); }}
          >
            <div style={dynamicStyles.gameIconBoxFixed}>
              <img src="/images/homepage/twins_icon.webp?v=2" alt="Match the Twins" style={dynamicStyles.gameIcon} />
            </div>
            <div style={dynamicStyles.gameInfo}>
              <div style={dynamicStyles.gameTitle}>Match the Twins</div>
              <div style={dynamicStyles.gameDesc}>Find the closest synonym</div>
            </div>
            <div style={{ ...dynamicStyles.gameArrow, background: '#EC4899' }}>
              <ArrowIcon size={20} />
            </div>
          </div>
        </div>

        {/* 5. Word of the Day - Image Matched Design */}
        <div 
          style={dynamicStyles.wotdCard}
          onClick={() => setIsDictionaryOpen(true)}
        >
          <div style={dynamicStyles.wotdLeft}>
            <div style={dynamicStyles.wotdLabelContainer}>
              <div style={dynamicStyles.wotdLabel}>WORD OF THE DAY</div>
              <span style={{ color: '#D4A373', fontSize: '12px' }}>✦</span>
            </div>
            
            <div style={dynamicStyles.wotdWord}>{wotd.word}</div>
            <div style={dynamicStyles.wotdMeaning}>{wotd.meaning}</div>
            
            <div style={dynamicStyles.wotdDashedLine} />

            <div style={dynamicStyles.wotdCTAButton}>
              <div style={dynamicStyles.wotdCTAIconBox}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5D4037" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
              </div>
              <span style={dynamicStyles.wotdCTAText}>Open Dictionary</span>
              <span style={{ fontSize: '14px', marginLeft: 'auto', fontWeight: '900' }}>→</span>
            </div>
          </div>

          <div style={dynamicStyles.wotdRight}>
            <img 
              src="/images/homepage/wotd_book_illustration.webp" 
              alt="WOTD Illustration" 
              style={dynamicStyles.wotdImgLarge} 
            />
          </div>
        </div>


      </div>

      {/* SETTINGS MODAL */}
      {isSettingsOpen && (
        <div style={dynamicStyles.modalOverlay} onClick={() => setIsSettingsOpen(false)}>
          <div style={dynamicStyles.settingsModal} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ fontSize: "24px", fontWeight: "900", color: colors.text, margin: 0 }}>Settings</h2>
              <button
                onClick={() => setIsSettingsOpen(false)}
                style={dynamicStyles.modalCloseBtn}
              >✕</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Sound Toggle */}
              <div style={dynamicStyles.settingsOption}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ fontSize: "20px" }}>{muted ? "🔇" : "🔊"}</span>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}>Game Sounds</span>
                    <span style={{ fontSize: "12px", color: colors.secondaryText }}>SFX and feedback</span>
                  </div>
                </div>
                <div
                  onClick={toggleSoundMute}
                  style={{
                    width: "56px", height: "32px", borderRadius: "16px",
                    background: muted ? (isDark ? "#334155" : "#E2E8F0") : "#14B8A6",
                    position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", borderRadius: "50%", background: "white",
                    position: "absolute", top: "4px", left: muted ? "4px" : "28px",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                  }} />
                </div>
              </div>

              {/* Theme Toggle */}
              <div style={dynamicStyles.settingsOption}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ fontSize: "20px" }}>{isDark ? "🌙" : "☀️"}</span>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}>Dark Mode</span>
                    <span style={{ fontSize: "12px", color: colors.secondaryText }}>{isDark ? "Night" : "Light"} theme</span>
                  </div>
                </div>
                <div
                  onClick={() => {
                    const next = isDark ? "light" : "dark";
                    setTheme(next);
                    localStorage.setItem("app_theme", next);
                  }}
                  style={{
                    width: "56px", height: "32px", borderRadius: "16px",
                    background: isDark ? "#14B8A6" : "#E2E8F0",
                    position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", borderRadius: "50%", background: "white",
                    position: "absolute", top: "4px", left: isDark ? "28px" : "4px",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                  }} />
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsSettingsOpen(false)}
              style={dynamicStyles.modalDoneBtn}
            >Done</button>
          </div>
        </div>
      )}

      {/* LOCAL DICTIONARY MODAL */}
      {isDictionaryOpen && (
        <div style={dynamicStyles.modalOverlay} onClick={() => setIsDictionaryOpen(false)}>
          <div style={dynamicStyles.dictionaryModal} onClick={e => e.stopPropagation()}>
            <div style={dynamicStyles.modalHeader}>
              <h2 style={{ fontSize: "28px", fontWeight: "900", color: colors.text, margin: 0, letterSpacing: '-1px' }}>Dictionary</h2>
              <button
                onClick={() => setIsDictionaryOpen(false)}
                style={dynamicStyles.modalCloseBtn}
              >✕</button>
            </div>

            <div style={dynamicStyles.searchContainer}>
              <input
                type="text"
                placeholder="Search for a word..."
                value={dictionarySearch}
                onChange={(e) => setDictionarySearch(e.target.value)}
                style={dynamicStyles.searchInput}
              />
            </div>

            <div style={dynamicStyles.wordList}>
              {filteredWords.length > 0 ? (
                filteredWords.map((wordObj) => (
                  <div key={wordObj.id} style={dynamicStyles.wordItem}>
                    <div style={dynamicStyles.wordTitle}>{wordObj.word}</div>
                    <div style={dynamicStyles.wordMeaning}>{wordObj.meaning}</div>
                  </div>
                ))
              ) : (
                <div style={dynamicStyles.emptyState}>
                  No words found matching "{dictionarySearch}"
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStyles = (theme) => {
  const isDark = theme === "dark";
  const colors = {
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#0F172A",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    border: isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0"
  };

  return {
    container: {
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingBottom: '60px',
      overflowX: 'hidden',
      background: isDark
        ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
        : 'linear-gradient(180deg, #1F8A8A 0%, #4FB6B2 25%, #9FE3DE 55%, #EAF7F6 85%, #F7FCFB 100%)',
      fontFamily: "'Inter', sans-serif",
    },
    topBar: {
      width: '100%',
      maxWidth: '500px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '16px 20px 12px',
      zIndex: 10,
    },
    profileCircle: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: isDark ? '#334155' : '#1AA39A',
      border: isDark ? '1.5px solid #475569' : '1.5px solid white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      padding: 0,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    avatarPlaceholder: {
      fontSize: '18px',
      fontWeight: '700',
      color: 'white',
    },
    content: {
      width: '100%',
      maxWidth: '500px',
      padding: '0 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    greetingLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      flexGrow: 1,
      marginRight: '12px',
      overflow: 'hidden',
    },
    greetingLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: isDark ? '#94A3B8' : 'rgba(255, 255, 255, 0.8)',
      letterSpacing: '0.08em',
    },
    usernameText: {
      fontSize: 'clamp(24px, 5vw, 32px)',
      fontWeight: '800',
      color: '#FFFFFF',
      lineHeight: 1.1,
      letterSpacing: '-0.5px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    heroCard: {
      background: isDark ? '#1E293B' : '#ffffff',
      borderRadius: '20px',
      boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.2)' : '0 8px 20px rgba(0,0,0,0.05)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      marginBottom: '8px',
      marginTop: '12px',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden',
      minHeight: '130px',
      border: isDark ? '1px solid #334155' : 'none',
    },
    heroRing: {
      width: '70px',
      height: '70px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '8px 0',
      position: 'relative',
    },
    heroRingInner: {
      width: '58px',
      height: '58px',
      borderRadius: '50%',
      background: isDark ? '#1E293B' : '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: '700',
      color: isDark ? '#94A3B8' : '#64748B',
      zIndex: 1,
    },
    heroBrain: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '144px',
      zIndex: 2,
      pointerEvents: 'none',
      objectFit: 'contain',
      opacity: isDark ? 0.8 : 1,
    },
    heroLeft: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      gap: '2px',
      maxWidth: '110px',
      zIndex: 1,
    },
    heroLabel: {
      fontSize: '10px',
      fontWeight: '600',
      color: isDark ? '#94A3B8' : '#64748B',
      letterSpacing: '0.05em',
      whiteSpace: 'nowrap',
      opacity: 0.9,
    },
    heroValue: {
      fontSize: '36px',
      fontWeight: '900',
      color: isDark ? '#F1F5F9' : '#0F172A',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      lineHeight: 1,
      margin: '2px 0',
      letterSpacing: '-1px',
    },
    heroSubtext: {
      fontSize: '11px',
      fontWeight: '500',
      color: isDark ? '#64748B' : '#94A3B8',
      whiteSpace: 'nowrap',
    },
    heroRight: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      gap: '4px',
      maxWidth: '90px',
      zIndex: 1,
      marginTop: '6px',
    },
    heroSubtextTeal: {
      fontSize: '11px',
      fontWeight: '700',
      color: '#1AA39A',
      whiteSpace: 'nowrap',
    },
    sectionHeader: {
      fontSize: '18px',
      fontWeight: '800',
      color: '#FFFFFF',
      marginTop: '4px',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      letterSpacing: '-0.2px',
      textTransform: 'uppercase',
      opacity: 0.9,
      paddingLeft: '4px',
    },
    gameList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    gameCard: {
      borderRadius: '22px',
      padding: '18px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      minHeight: '100px',
      cursor: 'pointer',
      boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.15)' : '0 4px 16px rgba(0,0,0,0.06)',
    },
    gameIconBoxFixed: {
      width: '88px',
      height: '88px',
      borderRadius: '14px',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
      flexShrink: 0,
    },
    gameIcon: {
      width: '100%', height: '100%',
      objectFit: 'cover',
    },
    gameInfo: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      justifyContent: 'center',
    },
    gameTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: isDark ? '#F1F5F9' : '#0F172A',
      letterSpacing: '-0.1px',
      marginBottom: '4px',
    },
    gameDesc: {
      fontSize: '13px',
      color: isDark ? '#94A3B8' : '#7A8594',
      fontWeight: '400',
      marginBottom: '8px',
    },
    gameArrow: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
      flexShrink: 0,
      marginLeft: 'auto',
    },
    wotdCard: {
      background: isDark ? '#1E293B' : '#FEF9F3',
      borderRadius: '32px',
      padding: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.2)' : '0 10px 40px rgba(139, 94, 60, 0.06)',
      marginBottom: '40px',
      minHeight: '170px',
      border: isDark ? '1px solid #334155' : '1px solid rgba(212, 163, 115, 0.15)',
      position: 'relative',
      cursor: 'pointer',
      overflow: 'hidden',
      width: '100%',
      boxSizing: 'border-box',
    },
    wotdLeft: {
      flex: '1.2',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 2,
    },
    wotdRight: {
      flex: '0.8',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      position: 'relative',
    },
    wotdImgLarge: {
      width: '120%',
      height: 'auto',
      objectFit: 'contain',
      transform: 'translate(10%, 5%)',
    },
    wotdLabelContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px',
    },
    wotdLabel: {
      fontSize: '11px',
      fontWeight: '800',
      color: isDark ? '#94A3B8' : '#B88B64',
      letterSpacing: '1.5px',
      position: 'relative',
    },
    wotdWord: {
      fontSize: '52px',
      fontWeight: '900',
      color: isDark ? '#F1F5F9' : '#2C1810',
      lineHeight: 1,
      letterSpacing: '-2px',
      marginBottom: '8px',
      fontFamily: 'serif', // Trying to mimic the serif look from image
    },
    wotdMeaning: {
      fontSize: '18px',
      color: isDark ? '#94A3B8' : '#5D4037',
      fontWeight: '600',
      lineHeight: 1.2,
      marginBottom: '24px',
      opacity: 0.85,
    },
    wotdDashedLine: {
      width: '100%',
      height: '1px',
      borderBottom: isDark ? '1px dashed #334155' : '1px dashed rgba(212, 163, 115, 0.2)',
      marginBottom: '20px',
      opacity: 0.5,
    },
    wotdCTAButton: {
      display: 'flex',
      alignItems: 'center',
      background: isDark ? 'rgba(255,255,255,0.03)' : '#FFF3E6',
      padding: '4px 10px',
      borderRadius: '24px',
      border: isDark ? '1px solid #334155' : '1px solid rgba(212, 163, 115, 0.15)',
      width: 'fit-content',
      minWidth: '110px',
      gap: '6px',
      color: '#5D4037',
      alignSelf: 'flex-start',
      marginTop: '4px',
    },
    wotdCTAIconBox: {
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: isDark ? 'rgba(255,255,255,0.05)' : '#FFE8CC',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    wotdCTAText: {
      fontSize: '11px',
      fontWeight: '700',
      color: isDark ? '#F1F5F9' : '#5D4037',
    },
    // Modal Styles
    modalOverlay: {
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: isDark ? "rgba(0, 0, 0, 0.8)" : "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 2000, padding: "20px"
    },
    settingsModal: {
      background: colors.surface, borderRadius: "32px", padding: "32px",
      width: "100%", maxWidth: "360px", boxShadow: isDark ? "0 25px 50px rgba(0,0,0,0.5)" : "0 25px 50px rgba(0,0,0,0.2)",
      display: "flex", flexDirection: "column", gap: "24px",
      border: isDark ? "1px solid #334155" : "none",
      animation: "modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
    },
    modalCloseBtn: {
      background: isDark ? "rgba(255,255,255,0.05)" : "#F1F5F9",
      border: "none", width: "32px", height: "32px", borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", color: colors.secondaryText
    },
    settingsOption: {
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "20px", background: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC",
      borderRadius: "20px", border: `1px solid ${colors.border}`
    },
    modalDoneBtn: {
      width: "100%", height: "56px", borderRadius: "18px",
      background: isDark ? "#1E293B" : "#111827", color: "white",
      border: isDark ? "1.5px solid #334155" : "none",
      fontSize: "16px", fontWeight: "700", cursor: "pointer",
      marginTop: "8px"
    },
    dictionaryModal: {
      background: colors.surface, borderRadius: "32px",
      width: "100%", maxWidth: "500px", height: "80vh",
      boxShadow: isDark ? "0 25px 50px rgba(0,0,0,0.5)" : "0 25px 50px rgba(0,0,0,0.2)",
      display: "flex", flexDirection: "column",
      border: isDark ? "1px solid #334155" : "none",
      animation: "modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
      overflow: "hidden"
    },
    modalHeader: {
      padding: "32px 32px 20px",
      display: "flex", justifyContent: "space-between", alignItems: "center"
    },
    searchContainer: {
      padding: "0 32px 20px"
    },
    searchInput: {
      width: "100%", padding: "16px 20px", borderRadius: "16px",
      border: `1.5px solid ${colors.border}`, background: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC",
      color: colors.text, fontSize: "16px", outline: "none",
      transition: "all 0.2s ease"
    },
    wordList: {
      flex: 1, overflowY: "auto", padding: "0 32px 32px",
      display: "flex", flexDirection: "column", gap: "12px"
    },
    wordItem: {
      padding: "16px 20px", borderRadius: "16px",
      background: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC",
      border: `1px solid ${colors.border}`
    },
    wordTitle: {
      fontSize: "18px", fontWeight: "700", color: colors.text, marginBottom: "4px"
    },
    wordMeaning: {
      fontSize: "14px", color: colors.secondaryText, lineHeight: 1.4
    },
    emptyState: {
      textAlign: "center", padding: "40px 0", color: colors.secondaryText, fontSize: "15px"
    }
  };
};

export default HomeScreen;
